import React, {useEffect, useState, useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'
import {Controller, useForm} from 'react-hook-form'
import {joiResolver} from '@hookform/resolvers/joi'
import {AiOutlineInfoCircle} from 'react-icons/ai'
import {FiChevronDown, FiChevronRight, FiMail, FiPenTool} from 'react-icons/fi'
import _ from 'lodash'
import {
  VmoAccordion,
  VmoAccordionContent,
  VmoAccordionTitle,
  VmoButton,
  VmoCard,
  VmoDivider,
  VmoForm,
  VmoFormCheckbox,
  VmoFormDropdown,
  VmoFormField,
  VmoFormGroup,
  VmoFormInput,
  VmoFormTextArea,
  VmoGrid,
  VmoGridColumn,
  VmoHeader,
  VmoHeaderContent,
  VmoIcon,
  VmoInput,
  VmoRadio,
  VmoTooltip,
} from 'vmo-library'

import {getAllMailInbox} from 'actions/inbox'
import {addSurvey, editSurvey, getSurvey} from 'actions/settings_productivity'
import {ADD_SURVEY, EDIT_SURVEY, GET_SURVEY} from 'actions/types'
import star from 'assets/star.png'
import star1 from 'assets/star1.png'
import star2 from 'assets/star2.png'
import dummy from 'assets/user.png'
import Page from 'components/common/Page'
import DummyAvtarAccountUser from 'components/common/DummyAvtar'
import {customerSatisfactionSchema} from 'validation/settings/productivity/customer-satisfaction.schema'
import useUserPermissions from 'hooks/pure/useUserPermission'
import useLearnMoreUrl from 'hooks/pure/useLearnMoreUrl'
import {INBOX_TYPES} from 'constants/enums'
import {SETTINGS_PATH} from 'constants/path'
import {getPathWithPrefix} from 'utils/helper'
import {Input, TextArea} from 'components/common/Form'
import {useUnsavedChanges} from 'hooks/pure/useUnsavedChanges'

const defaultValue = {
  auto: true,
  commentEnabled: true,
  commentEnabledBelow: 3,
  finishText: 'Thank you for your valuable feedback',
  footer: '',
  heading: '',
  mailboxIds: [],
  name: '',
  ratingsType: 'number',
  subHeading: '',
  subject: '',
  commentTitle: 'Can you tell us more about your experience?',
  description: '',
}

const ratingOption = [
  {
    value: 'star',
    text: 'Star Ratings',
    key: 'star',
  },
  {
    value: 'number',
    text: 'Number Ratings',
    key: 'number',
  },
]

function CustomerSatisfactionForm(props) {
  const {toggleCreate} = props
  const [listMailBox, setListMailBox] = useState([])
  const [profilePicture, setProfilePicture] = useState(null)
  const [userAccountImage, setUserAccountImage] = useState(false)
  const [uploadProfile, setUploadProfile] = useState(null)

  const {
    handleSubmit,
    formState: {errors, isDirty},
    control,
    watch,
    reset,
  } = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
    reValidateMode: 'onChange',
    submitFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(customerSatisfactionSchema),
    defaultValues: defaultValue,
  })
  const ratingType = watch('ratingsType')
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {id} = useParams()

  const {surveySingle} = useSelector(state => state.settingsProductivity)
  const {inbox: mailBoxList} = useSelector(state => state.mailInbox)
  const {type, isLoadingData, successLabels = []} = useSelector(state => state.apiReducer)

  const [activeIndex, setActiveIndex] = useState(0)
  const [cardOpen, setCardOpen] = useState(true)
  const [isRecentConversationOpen, setIsRecentConversationOpen] = useState(false)

  const {setWarningModal} = useUnsavedChanges()
  const {advanceFeaturesAccess, productivityPermissions} = useUserPermissions()
  const allowCsatSurveyFeature = advanceFeaturesAccess?.csatSurveys
  const allowManageCustomerSatisfactionRole = productivityPermissions?.manage_customer_satisfaction

  const {settingsLearnMoreUrl} = useLearnMoreUrl()
  const addCulstomerSatisfactionLearnMore = settingsLearnMoreUrl?.productivity?.customerSatisfaction?.addLearnMore
  const updateCulstomerSatisfactionLearnMore = settingsLearnMoreUrl?.productivity?.customerSatisfaction?.updateLearnMore

  const handleReset = () => {
    if (id) {
      reset(_.omit(surveySingle, ['id', 'accountId', 'isDeleted', 'isActive', 'logo', 'logoUrl']))
      setUploadProfile(_.get(surveySingle, 'logoUrl'))
    } else {
      reset(defaultValue)
    }
  }
  useEffect(() => {
    window.history.pushState(null, '', window.location.href)
    window.onpopstate = () => {
      if (toggleCreate) {
        toggleCreate(false)
      } else {
        navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.CUSTOMER_SATISFACTION))
      }
    }
    return () => (window.onpopstate = () => {})
  }, [])

  useEffect(() => {
    if (id) dispatch(getSurvey(id))
    if (!mailBoxList) dispatch(getAllMailInbox())
  }, [])

  useEffect(() => {
    if (id) {
      if (isDirty || profilePicture) {
        setWarningModal(true)
      } else {
        setWarningModal(false)
      }
    }
  }, [isDirty])

  useEffect(() => {
    if (mailBoxList) {
      const list = mailBoxList
        .filter(inbox => (inbox.type === INBOX_TYPES.EMAIL ? inbox.emailVerified : true))
        .map(({id, name = '', email = ''}) => ({
          key: id,
          value: id,
          content: (
            <div>
              {name}
              <small className="ml-2 vmo-lead">{email}</small>
            </div>
          ),
          text: name,
        }))
      setListMailBox(list)
    }
  }, [mailBoxList])

  useEffect(() => {
    if (successLabels.includes(GET_SURVEY)) {
      handleReset()
    }
    if (successLabels.includes(EDIT_SURVEY)) {
      dispatch(getSurvey(id))

      navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.CUSTOMER_SATISFACTION))
    }
  }, [successLabels])

  const surveySubmit = data => {
    const formData = new FormData()
    formData.append('auto', data.auto)
    formData.append('commentEnabled', data.commentEnabled ? data.commentEnabled : true)
    formData.append('commentEnabledBelow', data.commentEnabledBelow ? data.commentEnabledBelow : 3)
    formData.append('finishText', data.finishText)
    formData.append('footer', data.footer)
    formData.append('heading', data.heading)
    formData.append('name', data.name)
    formData.append('description', data.description)
    formData.append('ratingsType', data.ratingsType)
    formData.append('subHeading', data.subHeading)
    formData.append('subject', data.subject)
    formData.append('commentTitle', data.commentTitle)
    if (profilePicture) {
      formData.append('file', profilePicture)
    }
    formData.append('mailboxIds', _.toString(data.mailboxIds))
    if (id) {
      dispatch(editSurvey(id, formData))
      setWarningModal(false)
    } else {
      dispatch(addSurvey(formData))
    }
  }
  const commentEnableOptions = Array.from({length: 10}, (_, i) => {
    if (ratingType === 'number') {
      return {value: i, key: i, text: `${i}`}
    }
    return {value: i + 1, key: i + 1, text: `${i + 1} Star`}
  })

  return (
    <Page
      header={id ? 'Edit Customer Satisfaction' : 'Create Customer Satisfaction Survey'}
      headerbackbtn={{
        onClick: () => {
          if (id) {
            setWarningModal(false)
            navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.CUSTOMER_SATISFACTION))
            handleReset()
          } else {
            toggleCreate(false)
          }
        },
      }}
      subHeader=" Create a customer satisfaction survey and feedback form for your service."
      table={false}
      learnMoreUrl={id ? updateCulstomerSatisfactionLearnMore : addCulstomerSatisfactionLearnMore}
      headerDivider
      lockFeature={!allowCsatSurveyFeature}
      lockRole={!allowManageCustomerSatisfactionRole}
      actionButton={{
        cancel: {
          content: 'Cancel',
          disabled: isLoadingData && (type.includes(EDIT_SURVEY) || type.includes(ADD_SURVEY)),
          onClick: () => {
            if (id) {
              setWarningModal(false)
              navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.CUSTOMER_SATISFACTION))
              handleReset()
            } else {
              toggleCreate(false)
            }
          },
        },
        success: {
          content: id ? 'Update' : 'Create',
          loading: isLoadingData && (type.includes(EDIT_SURVEY) || type.includes(ADD_SURVEY)),
          ...(allowCsatSurveyFeature &&
            allowManageCustomerSatisfactionRole && {
              onClick: handleSubmit(data => surveySubmit(data)),
            }),
          disabled: profilePicture
            ? false
            : !isDirty || (isLoadingData && (type.includes(EDIT_SURVEY) || type.includes(ADD_SURVEY))),
        },
      }}
    >
      <VmoAccordion className="mb-5">
        <VmoCard fluid className="switchBox vmo-cardAccording">
          <VmoAccordionTitle
            active={activeIndex === 0}
            index={0}
            onClick={(e, titleProps) => {
              const {index} = titleProps
              const newIndex = activeIndex === index ? -1 : index
              setActiveIndex(newIndex)
              setCardOpen(!cardOpen)
            }}
          >
            <VmoHeader as="h4">
              <VmoIcon>
                <FiMail />
              </VmoIcon>
              <VmoHeaderContent>
                <label>General Info</label>
                <p className="vmo-lead">
                  Contains general and basic information regarding the customer satisfaction survey
                </p>
              </VmoHeaderContent>
              <span className="arwIcon">{cardOpen ? <FiChevronDown /> : <FiChevronRight />}</span>
            </VmoHeader>
          </VmoAccordionTitle>
          <VmoAccordionContent active={activeIndex === 0}>
            <VmoForm className="errorLabel">
              <Input
                name="name"
                control={control}
                label="Survey Name"
                tooltipProps={{content: 'Specify name of the survey'}}
                placeholder="Survey name..."
                maxLength={40}
                required
                width={8}
              />
              <TextArea
                name="description"
                control={control}
                label="Description"
                tooltipProps={{content: 'Specify brief description about the survey'}}
                maxLength={200}
                placeholder="Type description here..."
                width={8}
              />
              <Input
                name="subject"
                control={control}
                label="Mail Subject"
                tooltipProps={{content: 'Specify subject of the survey mail'}}
                placeholder="Subject..."
                maxLength={40}
                required
                width={8}
              />
              <VmoFormField required>
                <div className="info-header">
                  <label>Select Inbox</label>
                  <VmoTooltip
                    content="Inboxes in which you want to include this survey"
                    trigger={<AiOutlineInfoCircle />}
                  />
                </div>
                <Controller
                  control={control}
                  name="mailboxIds"
                  render={({field: {value, onChange}}) => (
                    <VmoFormDropdown
                      width={8}
                      fluid
                      multiple
                      selection
                      value={value}
                      options={listMailBox}
                      onChange={(e, {value}) => onChange(value)}
                      error={
                        errors.mailboxIds && {
                          content: errors.mailboxIds.message,
                        }
                      }
                    />
                  )}
                />
              </VmoFormField>

              <VmoFormGroup grouped>
                <label>Which Emails Should Have The Satisfaction Survey Link?</label>
                <VmoFormField>
                  <Controller
                    control={control}
                    name="auto"
                    render={({field: {value, onChange}}) => (
                      <>
                        <div className="my-2 info-header">
                          <VmoRadio
                            label="Emails sent after a ticket is Closed"
                            value
                            checked={value === true}
                            onChange={(e, {value}) => onChange(value)}
                          />
                          <VmoTooltip
                            content="It will send a survey email to customer after the ticket is closed and the issue is resolved."
                            trigger={<AiOutlineInfoCircle className="ml-2" />}
                          />
                        </div>
                        <div className="info-header">
                          <VmoRadio
                            label="Allow agents to add survey links to specific emails"
                            value={false}
                            checked={value === false}
                            onChange={(e, {value}) => onChange(value)}
                          />
                          <VmoTooltip
                            content="It can allow custom settings to add survey links to specific emails."
                            trigger={<AiOutlineInfoCircle className="ml-2" />}
                          />
                        </div>
                      </>
                    )}
                  />
                </VmoFormField>
              </VmoFormGroup>
            </VmoForm>
          </VmoAccordionContent>
        </VmoCard>

        <VmoCard fluid className="switchBox vmo-cardAccording">
          <VmoAccordionTitle
            active={activeIndex === 1}
            index={1}
            onClick={(e, titleProps) => {
              const {index} = titleProps
              const newIndex = activeIndex === index ? -1 : index
              setActiveIndex(newIndex)
              setIsRecentConversationOpen(!isRecentConversationOpen)
            }}
          >
            <VmoHeader as="h4">
              <VmoIcon>
                <FiPenTool />
              </VmoIcon>
              <VmoHeaderContent>
                <label>Survey Customization</label>
                <p className="vmo-lead">
                  This question helps assess the overall satisfaction for the response provided by the agent
                </p>
              </VmoHeaderContent>
              <span className="arwIcon">{isRecentConversationOpen ? <FiChevronDown /> : <FiChevronRight />}</span>
            </VmoHeader>
          </VmoAccordionTitle>
          <VmoAccordionContent active={activeIndex === 1}>
            <VmoForm className="errorLabel">
              <VmoFormField>
                <div className="info-header">
                  <label>Rating Type</label>
                  <VmoTooltip
                    content="Types of rating you wish to get from the user for surveys"
                    trigger={<AiOutlineInfoCircle />}
                  />
                </div>
                <Controller
                  control={control}
                  name="ratingsType"
                  render={({field: {value, onChange}}) => (
                    <VmoFormDropdown
                      width={6}
                      selection
                      value={value}
                      options={ratingOption}
                      onChange={(e, {value}) => onChange(value)}
                    />
                  )}
                />
              </VmoFormField>
              <div className="ruleBox">
                <div className="info-header">
                  <label>Email Preview</label>
                  <VmoTooltip
                    content="Preview of the email the customer will receive requesting for ratings and feedback"
                    trigger={<AiOutlineInfoCircle />}
                  />
                </div>
                <div className="starRatPre">
                  <div className="agentAvtar1">
                    {/* {!userAccountImage ? ( */}
                    <img src={uploadProfile || dummy} className="img-fluid" alt="img" />
                    {/* ) : (
                      <DummyAvtarAccountUser />
                    )} */}
                  </div>
                  <VmoButton basic className="fileBtn" size="small">
                    Logo
                    <input
                      type="file"
                      accept=".png, .jpg, .jpeg"
                      key={uploadProfile}
                      onInput={e => {
                        if (e.target.files) {
                          setUserAccountImage(false)
                          setProfilePicture(e.target.files[0])
                          setUploadProfile(URL.createObjectURL(e.target.files[0]))
                        }
                      }}
                    />
                  </VmoButton>
                  <Controller
                    control={control}
                    name="heading"
                    render={({field: {value, onChange}}) => (
                      <VmoFormInput
                        type="text"
                        size="huge"
                        transparent
                        fluid
                        placeholder="Write Heading Here..."
                        onChange={e => onChange(e.target.value)}
                        value={value}
                        error={
                          errors.heading && {
                            content: errors.heading.message,
                          }
                        }
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="subHeading"
                    render={({field: {value, onChange}}) => (
                      <VmoFormInput
                        type="text"
                        size="huge"
                        transparent
                        fluid
                        placeholder="Write Sub Heading Here..."
                        onChange={e => onChange(e.target.value)}
                        value={value}
                        error={
                          errors.subHeading && {
                            content: errors.subHeading.message,
                          }
                        }
                      />
                    )}
                  />

                  <div className="RatprevBox">
                    <VmoGrid className="starTable" columns="equal">
                      {ratingType === 'number'
                        ? Array.from({length: 10}, (_, i) => {
                            let name = 'numberRating'
                            if (i > 4 && i < 7) name += ' numberRatingWarning'
                            if (i > 6) name += ' numberRatingGreen'
                            return (
                              <VmoGridColumn key={i}>
                                <div className={name}>{i.toString().padStart(2, '0')}</div>
                              </VmoGridColumn>
                            )
                          })
                        : Array.from({length: 10}, (_, i) => {
                            let source = star
                            if (i > 4 && i < 7) source = star1
                            if (i > 6) source = star2
                            return (
                              <VmoGridColumn key={i}>
                                <img alt="img" src={source} />
                              </VmoGridColumn>
                            )
                          })}
                    </VmoGrid>
                    <VmoGrid className="starTable">
                      <VmoGridColumn width={2}>
                        <VmoInput disabled transparent type="text" className="emailStarText" value="Bad" />
                      </VmoGridColumn>
                      <VmoGridColumn width={12} />
                      <VmoGridColumn width={2}>
                        <VmoInput disabled transparent type="text" className="emailStarText" value="Good" />
                      </VmoGridColumn>
                    </VmoGrid>
                  </div>

                  <div className="ratingFooter">
                    <Controller
                      control={control}
                      name="footer"
                      render={({field: {value, onChange}}) => (
                        <VmoFormInput
                          placeholder="Write down footer text here... e.g. All rights are reserved by VeemoApp"
                          onChange={e => onChange(e.target.value)}
                          value={value}
                          error={
                            errors.footer && {
                              content: errors.footer.message,
                            }
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              <VmoDivider className="my-5" />
              <VmoFormField>
                <h4 className="mb-0">Thank You Page</h4>
                <p className="vm-lead">Your portal layout will be applied to this page</p>
                <Controller
                  control={control}
                  name="finishText"
                  render={({field: {value, onChange}}) => (
                    <VmoFormInput
                      type="text"
                      maxLength={60}
                      name="question"
                      id="question"
                      onChange={e => onChange(e.target.value)}
                      value={value}
                      placeholder="Thank you for your valuable feedback."
                      error={
                        errors.finishText && {
                          content: errors.finishText.message,
                        }
                      }
                    />
                  )}
                />
              </VmoFormField>
              <VmoFormField>
                <label>If this is enabled , the customer can also provide written feedback if he/she wishes.</label>
                <Controller
                  control={control}
                  name="commentEnabled"
                  render={({field: {value, onChange}}) => (
                    <VmoFormCheckbox
                      checked={value}
                      label="Include additional comments box"
                      onChange={(e, {checked}) => onChange(checked)}
                    />
                  )}
                />
              </VmoFormField>
              <VmoFormField>
                <label>Include comment box if rating is below</label>
                <Controller
                  control={control}
                  name="commentEnabledBelow"
                  render={({field: {value, onChange}}) => (
                    <VmoFormDropdown
                      selection
                      fluid
                      width="2"
                      value={value}
                      options={commentEnableOptions}
                      onChange={(e, {value}) => onChange(value)}
                    />
                  )}
                />
              </VmoFormField>
              <Input
                name="commentTitle"
                control={control}
                label="Comment Box Title"
                placeholder="sorry for inconvenience, write us to better satisfaction..."
                maxLength={60}
              />
            </VmoForm>
          </VmoAccordionContent>
        </VmoCard>
      </VmoAccordion>
    </Page>
  )
}

export default CustomerSatisfactionForm
